// types
import type { EmployeeDutyData } from 'RestAPIs/cost-centers/employees/types';
import type { OpenRequestResponse } from 'RestAPIs/roster/open-requests/types';
import type { RosterPlanFilterSelection } from '../RosterPlan.types';

// utils
import {
  anyOptionIdEqualsAsString,
  anyOptionLabelEquals,
  buildLabelSet,
  normalizeToOptionArray
} from './rosterPlanFilterOptions';

export const applyRosterPlanFilters = ({
  employees,
  openRequests,
  appliedFilters
}: {
  employees?: EmployeeDutyData[];
  openRequests?: OpenRequestResponse[];
  appliedFilters: RosterPlanFilterSelection[];
}): {
  employeesData: EmployeeDutyData[];
  openRequestsData: OpenRequestResponse[];
} => {
  if (appliedFilters.length === 0) {
    return {
      employeesData: employees ?? [],
      openRequestsData: openRequests ?? []
    };
  }

  let filteredEmployeesData = employees ?? [];
  let filteredOpenRequestsData = openRequests ?? [];

  appliedFilters.forEach((appliedFilter) => {
    const selectedOptions = normalizeToOptionArray(appliedFilter.value);
    if (selectedOptions.length === 0) return;

    switch (appliedFilter.key) {
      // ----------------- EMPLOYEE GRID -----------------
      case 'costCenter': {
        filteredEmployeesData = filteredEmployeesData.filter((employee) =>
          anyOptionLabelEquals(selectedOptions, employee.customerName)
        );
        break;
      }

      case 'function': {
        filteredEmployeesData = filteredEmployeesData.filter((employee) =>
          anyOptionLabelEquals(selectedOptions, employee.benefits?.functionName)
        );
        break;
      }

      case 'employees': {
        filteredEmployeesData = filteredEmployeesData.filter((employee) =>
          anyOptionLabelEquals(selectedOptions, employee.nameInformal)
        );
        break;
      }

      case 'planned': {
        const wantsPlanned = selectedOptions.some(
          (option) => option.id === 'planned'
        );
        const wantsNotPlanned = selectedOptions.some(
          (option) => option.id === 'notPlanned'
        );

        filteredEmployeesData = filteredEmployeesData.filter((employee) => {
          const hasAnyPlanning = (employee.days?.length ?? 0) > 0;
          return (
            (wantsPlanned && hasAnyPlanning) ||
            (wantsNotPlanned && !hasAnyPlanning)
          );
        });
        break;
      }

      case 'types': {
        filteredEmployeesData = filteredEmployeesData.filter((employee) =>
          (employee.types ?? []).some((employeeTypeId) =>
            anyOptionIdEqualsAsString(selectedOptions, employeeTypeId)
          )
        );
        break;
      }

      case 'qualifications': {
        filteredEmployeesData = filteredEmployeesData.filter((employee) =>
          (employee.qualifications ?? []).some((qualificationId) =>
            anyOptionIdEqualsAsString(selectedOptions, qualificationId)
          )
        );
        break;
      }

      case 'dutyCodes': {
        const wantedLabels = buildLabelSet(selectedOptions);
        filteredEmployeesData = filteredEmployeesData.filter(
          (employee) =>
            employee.days?.some(
              (day) =>
                day.duties?.some((duty) =>
                  wantedLabels.has(duty.displayDutyCode ?? '')
                )
            )
        );
        break;
      }

      case 'dutyHourTypes': {
        const wantedLabels = buildLabelSet(selectedOptions);
        filteredEmployeesData = filteredEmployeesData.filter(
          (employee) =>
            employee.days?.some(
              (day) =>
                day.duties?.some(
                  (duty) =>
                    duty.periods?.some((period) =>
                      wantedLabels.has(period.dutyHourType?.code)
                    )
                )
            )
        );
        break;
      }

      case 'absences': {
        const wantsAbsencePeriods = selectedOptions.some(
          (option) => option.id === 'absences'
        );
        const wantsLeaveRequests = selectedOptions.some(
          (option) => option.id === 'leaveRequests'
        );

        filteredEmployeesData = filteredEmployeesData.filter((employee) => {
          const hasAbsences = employee.days?.some((day) => !!day.absences);
          const hasLeaveRequests = employee.days?.some(
            (day) => !!day.availabilities
          );
          return (
            (wantsAbsencePeriods && hasAbsences) ||
            (wantsLeaveRequests && hasLeaveRequests)
          );
        });
        break;
      }

      // -------------- OPEN REQUESTS GRID ---------------
      case 'costCenterOpenRequests': {
        filteredOpenRequestsData = filteredOpenRequestsData.filter((request) =>
          anyOptionLabelEquals(selectedOptions, request.costCenterName)
        );
        break;
      }

      case 'functionOpenRequests': {
        filteredOpenRequestsData = filteredOpenRequestsData.filter((request) =>
          anyOptionLabelEquals(selectedOptions, request.functionName)
        );
        break;
      }

      case 'qualificationsOpenRequests': {
        filteredOpenRequestsData = filteredOpenRequestsData.filter((request) =>
          (request.qualificationIds ?? []).some((qualificationId) =>
            anyOptionIdEqualsAsString(selectedOptions, qualificationId)
          )
        );
        break;
      }

      case 'dutyCodesOpenRequests': {
        const wantedLabels = buildLabelSet(selectedOptions);
        filteredOpenRequestsData = filteredOpenRequestsData.filter((request) =>
          wantedLabels.has(request.displayDutyCode)
        );
        break;
      }

      case 'dutyHourTypesOpenRequests': {
        const wantedLabels = buildLabelSet(selectedOptions); // dutyHourType.code
        filteredOpenRequestsData = filteredOpenRequestsData.filter(
          (request) =>
            request.periods?.some((period) =>
              wantedLabels.has(period.dutyHourType?.code)
            )
        );
        break;
      }

      case 'statusOpenRequests': {
        const wantsOpen = selectedOptions.some(
          (option) => option.id === 'open'
        );
        const wantsSendToFlex = selectedOptions.some(
          (option) => option.id === 'sendToFlex'
        );
        const wantsOpenForSelfMatch = selectedOptions.some(
          (option) => option.id === 'openForSelfMatch'
        );
        const wantsSubscribeSelfMatch = selectedOptions.some(
          (option) => option.id === 'subscribeSelfMatch'
        );

        filteredOpenRequestsData = filteredOpenRequestsData.filter(
          (request) => {
            const isOpen =
              request.statusCode === 'open' &&
              !request.employeeCostCenterActions;
            const isSend = request.statusCode === 'send';
            const isOpenSelf = !!request.employeeCostCenterActions?.length;
            const isSubscribedSelf =
              request.statusCode === 'bpm' && request.hasSubscribers;

            return (
              (wantsOpen && isOpen) ||
              (wantsSendToFlex && isSend) ||
              (wantsOpenForSelfMatch && isOpenSelf) ||
              (wantsSubscribeSelfMatch && isSubscribedSelf)
            );
          }
        );
        break;
      }

      default:
        break;
    }
  });

  return {
    employeesData: filteredEmployeesData,
    openRequestsData: filteredOpenRequestsData
  };
};
